/**
 * Submissions API service.
 *
 * Handles admin submission listing and deletion.
 *
 * @module submissionsApi
 */

import { apiGet, apiDelete } from '@/lib/apiUtils';
import { toast } from 'sonner';
import type { PaginatedResponse } from '@/types/pagination';
import type { UserSubmission } from '@/components/admin/answersManager/types';
import type { RatingDetail } from '@/lib/ratingsApi';

const EMPTY_SUBMISSIONS_PAGE: PaginatedResponse<UserSubmission> = {
  items: [],
  total: 0,
  page: 1,
  totalPages: 0,
  limit: 20,
};

export const fetchSubmissions = async (
  filterExamType: string,
  filterModule: string,
  filterGraded: string,
  filterCompletion: string,
  page: number = 1,
  limit: number = 20,
): Promise<PaginatedResponse<UserSubmission>> => {
  const params = new URLSearchParams();
  if (filterExamType !== 'all') params.append('exam_type', filterExamType);
  if (filterModule !== 'all') params.append('module', filterModule);
  if (filterGraded !== 'all') params.append('graded', filterGraded);
  if (filterCompletion !== 'all') params.append('completion', filterCompletion);

  params.append('page', page.toString());
  params.append('limit', limit.toString());

  const result = await apiGet<PaginatedResponse<UserSubmission>>(
    `/admin/submissions?${params}`,
    {
      errorMessage: 'Failed to fetch submissions',
      context: 'fetchSubmissions',
      errorReturnValue: EMPTY_SUBMISSIONS_PAGE,
    },
  );

  return result ?? EMPTY_SUBMISSIONS_PAGE;
};

export const fetchSubmissionItemRatings = async (
  sessionId: string,
  module: string,
  targetIds: string[],
): Promise<RatingDetail[]> => {
  if (targetIds.length === 0) return [];
  const query = encodeURIComponent(targetIds.join(','));
  const result = await apiGet<RatingDetail[]>(
    `/admin/submissions/${encodeURIComponent(sessionId)}/module/${encodeURIComponent(module)}/item-ratings?targetIds=${query}`,
    {
      errorMessage: 'Failed to load learner feedback',
      context: 'fetchSubmissionItemRatings',
    },
  );
  return result ?? [];
};

export const deleteSubmission = async (sessionId: string): Promise<boolean> => {
  const result = await apiDelete(`/admin/submissions/${sessionId}`, {
    errorMessage: 'Failed to delete submission',
    context: 'deleteSubmission',
    errorReturnValue: false,
  });

  if (result === false) {
    return false;
  }

  toast.success('Submission deleted successfully');
  return true;
};
